import { FC, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { ClaimLinkCandidate, ClaimLinkRole } from "../../../../../types/claims";
import { apiService } from "../../../../../services/apiService";
import {
  formatCurrency,
  formatDisplayDate,
} from "../../../../helpers/dataHelper";
import CoreInput from "../../../../ui/core/CoreInput";
import CardChip from "../CardChip";
import { FieldError, Hint } from "../record-payment/ClaimEditDialogParts";
import {
  MATCH_REASON_LABELS,
  ROLE_LABELS,
  claimTag,
  describeCandidate,
  parseClaimLinkError,
} from "./claimLinks";

/** The other claim the picker settled on. */
export interface LinkSelection {
  candidate: ClaimLinkCandidate;
  /** Why it was preselected, for the best fit. */
  reason: string | null;
  confirmDifferentPatient: boolean;
}

/** A selection that can be reviewed: a different patient has to be confirmed. */
export const isSelectionReady = (selection: LinkSelection | null): boolean =>
  !!selection &&
  (!selection.candidate.differentPatient || selection.confirmDifferentPatient);

const select = (
  candidate: ClaimLinkCandidate,
  reason: string | null = null,
): LinkSelection => ({ candidate, reason, confirmDifferentPatient: false });

const toAmount = (value: ClaimLinkCandidate["billedAmount"]) => {
  const amount = Number(value);
  return value === null || value === "" || isNaN(amount) ? null : amount;
};

const CandidateOption: FC<{
  candidate: ClaimLinkCandidate;
  bestFitReason: string | null;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}> = ({ candidate, bestFitReason, selected, disabled, onSelect }) => {
  const billed = toAmount(candidate.billedAmount);
  const reasons = candidate.matchReasons
    .map((reason) => MATCH_REASON_LABELS[reason])
    .filter(Boolean);
  return (
    <Box
      component="label"
      data-testid={`link-candidate-${candidate.claimNumber}`}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        p: 1.25,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <Radio
        size="small"
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
        value={candidate.claimNumber}
        sx={{ p: 0.5 }}
        inputProps={{ "aria-label": `Claim ${candidate.claimNumber}` }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
            {claimTag(candidate.claimNumber)}
          </Typography>
          {bestFitReason && (
            <CardChip chip={{ label: "Best fit", kind: "success" }} />
          )}
          {candidate.differentPatient && (
            <CardChip chip={{ label: "Different patient", kind: "warning" }} />
          )}
        </Box>
        <Typography sx={{ fontSize: "0.8125rem" }}>
          {describeCandidate(candidate)}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          {[
            candidate.dateSubmitted &&
              `Submitted ${formatDisplayDate(candidate.dateSubmitted)}`,
            billed !== null && `Billed ${formatCurrency(billed)}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
        {bestFitReason ? (
          <Typography sx={{ fontSize: "0.75rem", color: "success.main" }}>
            {bestFitReason}
          </Typography>
        ) : reasons.length ? (
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {reasons.join(" · ")}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

interface ClaimLinkPickerProps {
  claimId: string;
  role: ClaimLinkRole;
  /** Omitted when the role is fixed (right after recording the action: this claim is the original). */
  onRoleChange?: (role: ClaimLinkRole) => void;
  /** The roles this claim can take; the others are shown disabled. */
  availableRoles?: ClaimLinkRole[];
  /** Why the role was preselected. */
  roleHint?: string;
  value: LinkSelection | null;
  onChange: (value: LinkSelection | null) => void;
  /** A refusal of the chosen claim from the link call. */
  error?: string | null;
  disabled?: boolean;
}

/**
 * Picks the other side of a correct and resubmit (REV-2629): the role, then
 * the other claim, from the API's ranked candidates (best fit preselected with
 * its reason; same date of service by default) or by typing its number. A
 * claim for a different patient has to be confirmed; a typed number that isn't
 * in our system is refused by the API.
 */
const ClaimLinkPicker: FC<ClaimLinkPickerProps> = ({
  claimId,
  role,
  onRoleChange,
  availableRoles,
  roleHint,
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const [mode, setMode] = useState<"list" | "typed">("list");
  const [sameDateOfService, setSameDateOfService] = useState(true);
  const [candidates, setCandidates] = useState<ClaimLinkCandidate[]>([]);
  const [bestFit, setBestFit] = useState<string | null>(null);
  const [bestFitReason, setBestFitReason] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [typedNumber, setTypedNumber] = useState("");
  const [typedError, setTypedError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // A slow lookup mustn't land after the user typed another number
  const lookupSeqRef = useRef(0);

  useEffect(() => {
    if (mode !== "list" || !claimId) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    void apiService
      .getClaimLinkCandidates(claimId, { role, sameDateOfService })
      .then((response) => {
        if (cancelled) return;
        if (!response.success || !response.data) {
          setCandidates([]);
          setBestFit(null);
          setBestFitReason(null);
          setLoadError(parseClaimLinkError(response).message);
          onChangeRef.current(null);
          return;
        }
        const data = response.data;
        setCandidates(data.candidates);
        setBestFit(data.bestFit);
        setBestFitReason(data.bestFitReason);
        // A new list replaces the choice: the best fit, when the API names one
        const best = data.candidates.find(
          (candidate) => candidate.claimNumber === data.bestFit,
        );
        onChangeRef.current(best ? select(best, data.bestFitReason) : null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [claimId, role, sameDateOfService, mode]);

  const switchMode = (next: "list" | "typed") => {
    setMode(next);
    setTypedError(null);
    onChange(null);
    setTypedNumber("");
  };

  const lookUp = async () => {
    const number = typedNumber.trim();
    if (!number) {
      setTypedError("Enter the claim number.");
      return;
    }
    const seq = ++lookupSeqRef.current;
    setIsLookingUp(true);
    setTypedError(null);
    onChange(null);
    try {
      const response = await apiService.getClaimLinkCandidates(claimId, {
        role,
        sameDateOfService: false,
        claimNumber: number,
      });
      if (seq !== lookupSeqRef.current) return;
      const found = response.success ? response.data?.candidates[0] : null;
      if (found) {
        onChange(select(found));
        return;
      }
      setTypedError(
        response.success
          ? `Claim ${claimTag(number)} is not in our system yet.`
          : parseClaimLinkError(response).message,
      );
    } finally {
      if (seq === lookupSeqRef.current) setIsLookingUp(false);
    }
  };

  const otherSide = role === "original" ? "corrected" : "original";
  const isBusy = disabled || isLookingUp;
  const typedCandidate = mode === "typed" ? value?.candidate : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {onRoleChange && (
        <Box>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, mb: 0.5 }}>
            Role
          </Typography>
          <RadioGroup
            aria-label="Role"
            value={role}
            onChange={(e) => {
              setTypedError(null);
              onRoleChange(e.target.value as ClaimLinkRole);
            }}
          >
            {(Object.keys(ROLE_LABELS) as ClaimLinkRole[]).map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                disabled={
                  disabled ||
                  (!!availableRoles && !availableRoles.includes(option))
                }
                control={<Radio size="small" />}
                // A role the claim can't take reads as unavailable, not just greyed text
                sx={{ "&.Mui-disabled": { opacity: 0.5 } }}
                label={
                  <Typography sx={{ fontSize: "0.8125rem" }}>
                    {ROLE_LABELS[option]}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
          {roleHint && <Hint>{roleHint}</Hint>}
        </Box>
      )}

      <Box>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, mb: 0.5 }}>
          The {otherSide} claim
        </Typography>

        {mode === "list" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={sameDateOfService}
                  disabled={disabled}
                  onChange={(e) => setSameDateOfService(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.8125rem" }}>
                  Same date of service only
                </Typography>
              }
            />

            {isLoading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center", py: 2 }}
                aria-label="Loading candidates"
              >
                <CircularProgress size={20} />
              </Box>
            ) : loadError ? (
              <Alert severity="error" sx={{ maxWidth: "100%" }}>
                {loadError}
              </Alert>
            ) : candidates.length ? (
              <RadioGroup
                aria-label="Candidate claims"
                value={value?.candidate.claimNumber ?? ""}
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
              >
                {candidates.map((candidate) => {
                  const reason =
                    candidate.claimNumber === bestFit ? bestFitReason : null;
                  return (
                    <CandidateOption
                      key={candidate.claimNumber}
                      candidate={candidate}
                      bestFitReason={reason}
                      selected={
                        value?.candidate.claimNumber === candidate.claimNumber
                      }
                      disabled={disabled}
                      onSelect={() => onChange(select(candidate, reason))}
                    />
                  );
                })}
              </RadioGroup>
            ) : (
              <Alert severity="info" sx={{ maxWidth: "100%" }}>
                No matching claims for this patient
                {sameDateOfService ? " on the same date of service" : ""}. Enter
                the claim number instead.
              </Alert>
            )}

            <Box>
              <Button
                size="small"
                onClick={() => switchMode("typed")}
                disabled={disabled}
                sx={{ textTransform: "none", px: 0, color: "custom.brandTeal" }}
              >
                Enter a claim number instead
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Box sx={{ flex: 1 }}>
                <CoreInput
                  placeholder="Claim number"
                  value={typedNumber}
                  disabled={isBusy}
                  onChange={(e) => {
                    setTypedNumber(e.target.value);
                    setTypedError(null);
                    // A looked-up claim no longer matches the number
                    if (value) onChange(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void lookUp();
                    }
                  }}
                  inputProps={{ "aria-label": "Claim number" }}
                />
              </Box>
              <Button
                variant="outlined"
                onClick={() => void lookUp()}
                disabled={disabled}
                loading={isLookingUp}
              >
                Find
              </Button>
            </Box>
            <FieldError message={typedError ?? undefined} />
            {typedCandidate && (
              <CandidateOption
                candidate={typedCandidate}
                bestFitReason={null}
                selected
                disabled={disabled}
                onSelect={() => undefined}
              />
            )}
            <Box>
              <Button
                size="small"
                onClick={() => switchMode("list")}
                disabled={isBusy}
                sx={{ textTransform: "none", px: 0, color: "custom.brandTeal" }}
              >
                Choose from the suggested claims
              </Button>
            </Box>
          </Box>
        )}
        <FieldError message={error ?? undefined} />
      </Box>

      {value?.candidate.differentPatient && (
        <Alert severity="warning" sx={{ maxWidth: "100%" }}>
          <Typography sx={{ fontSize: "0.8125rem" }}>
            {claimTag(value.candidate.claimNumber)} is for a different patient
            {value.candidate.patientName
              ? ` (${value.candidate.patientName})`
              : ""}
            .
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={value.confirmDifferentPatient}
                disabled={disabled}
                onChange={(e) =>
                  onChange({
                    ...value,
                    confirmDifferentPatient: e.target.checked,
                  })
                }
              />
            }
            label={
              <Typography sx={{ fontSize: "0.8125rem" }}>
                It's the right claim: link it anyway
              </Typography>
            }
          />
        </Alert>
      )}
    </Box>
  );
};

export default ClaimLinkPicker;
